export const ASSISTANT_ACTION_IDS = [
  "action.scene.enter",
  "action.task.attempt",
  "action.task.retry",
  "action.task.reset",
  "action.item.select",
  "action.item.place",
  "action.control.change",
  "action.evidence.open",
  "action.assistant.ask"
] as const;

export const ASSISTANT_VALUE_IDS = [
  "value.none",
  "value.selected",
  "value.placed",
  "value.adjusted",
  "value.submitted",
  "value.corrected"
] as const;

export type AssistantActionId = (typeof ASSISTANT_ACTION_IDS)[number];
export type AssistantValueId = (typeof ASSISTANT_VALUE_IDS)[number];

const COMMON_LEARNING_ACTIONS = [
  "action.scene.enter",
  "action.task.attempt",
  "action.task.retry",
  "action.evidence.open",
  "action.assistant.ask"
] as const satisfies readonly AssistantActionId[];

const withCommonActions = (
  ...actionIds: readonly AssistantActionId[]
): readonly AssistantActionId[] => [...COMMON_LEARNING_ACTIONS, ...actionIds];

/**
 * Each scene exposes only the action categories its UI can produce. This is
 * shared by the browser snapshot builder and the server-side authorization
 * layer so a valid global action cannot be replayed in an unrelated scene.
 */
export const ASSISTANT_SCENE_ACTIONS = {
  "C1-00": withCommonActions("action.item.select", "action.control.change"),
  "C1-01": withCommonActions("action.task.reset", "action.item.select", "action.item.place"),
  "C1-02": withCommonActions("action.item.select"),
  "C1-03": withCommonActions("action.item.select"),
  "C2-00": withCommonActions("action.item.select", "action.control.change"),
  "C2-01": withCommonActions("action.item.select"),
  "C2-03": withCommonActions("action.item.select"),
  "C3-00": withCommonActions("action.control.change"),
  "C3-01": withCommonActions("action.item.select", "action.control.change"),
  "C3-02": withCommonActions("action.item.select", "action.control.change"),
  "C3-03": withCommonActions("action.item.select", "action.control.change"),
  "C3-04": withCommonActions("action.item.select", "action.control.change"),
  "C3-05": withCommonActions("action.item.select", "action.item.place"),
  "C4-01": withCommonActions("action.task.reset", "action.item.select", "action.item.place"),
  "C4-03": withCommonActions("action.item.select"),
  F01: withCommonActions("action.item.select"),
  F02: withCommonActions("action.item.select", "action.item.place"),
  F03: ["action.scene.enter"]
} as const;

export type AssistantPolicySceneId = keyof typeof ASSISTANT_SCENE_ACTIONS;

export const ASSISTANT_ACTION_VALUES = {
  "action.scene.enter": ["value.none"],
  "action.task.attempt": ["value.submitted"],
  "action.task.retry": ["value.submitted", "value.corrected"],
  "action.task.reset": ["value.none"],
  "action.item.select": ["value.selected"],
  "action.item.place": ["value.placed"],
  "action.control.change": ["value.adjusted"],
  "action.evidence.open": ["value.selected"],
  "action.assistant.ask": ["value.none"]
} as const satisfies Readonly<Record<AssistantActionId, readonly AssistantValueId[]>>;

export function getAssistantSceneActionIds(
  sceneId: AssistantPolicySceneId
): readonly AssistantActionId[] {
  return ASSISTANT_SCENE_ACTIONS[sceneId];
}

export function getAssistantSceneValueIds(
  sceneId: AssistantPolicySceneId
): readonly AssistantValueId[] {
  const values = getAssistantSceneActionIds(sceneId)
    .flatMap((actionId) => ASSISTANT_ACTION_VALUES[actionId]);
  return ASSISTANT_VALUE_IDS.filter((valueId) => values.includes(valueId));
}

export function isAssistantActionValueCompatible(
  actionId: AssistantActionId,
  valueId: AssistantValueId | undefined
): boolean {
  if (!valueId) return true;
  return ASSISTANT_ACTION_VALUES[actionId].includes(valueId as never);
}
